import dynamic from 'next/dynamic';
import {ReactNode, useState, useEffect, useRef, FC} from 'react';
import Loader from '@jobseeker/lotus/components/Loader';
import {
  HOME,
  JOBDETAIL,
  EPOCH,
  COURSE,
  TAG,
  SEARCH,
  JOBFEED,
  SITEMAP,
} from '@constants';
import {shallowEqual, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import ScrollToTop from '@jobseeker/lotus/components/ScrollToTop';
import {Box, useTheme} from '@mui/material';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import useInfoBannerHeight from '@jobseeker/common-core/hooks/useInfoBannerHeight';
import {utilServerSideDeviceDetection} from '@utils';
import Promotions from '@jobseeker/feature-ads/components/Promotions';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';
import HiristPromotions from '@jobseeker/feature-ads/components/HiristPromotions';
import cookie from 'cookie';
import {getItemWithExpiry} from '@jobseeker/common-core/utils';
import TalentBridgePromoSlider from '@components/TalentBridge/TalentBridgePromoSlider';

const HeaderAuth = dynamic(
  () =>
    import('@jobseeker/client-harness/components/NavigationHarness/HeaderAuth'),
  {
    loading: () => <Loader />,
  }
);

const HeaderNonAuth = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/HeaderNonAuth'
    ),
  {
    loading: () => <Loader />,
  }
);

const CookieBanner = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/CookieBanner'
    ),
  {
    loading: () => <Loader />,
  }
);

const Footer = dynamic(
  () => import('@jobseeker/client-harness/components/NavigationHarness/Footer'),
  {
    loading: () => <Loader />,
  }
);

const HomePageFooter = dynamic(
  () => import('@components/HomeV2/homePageFooter'),
  {ssr: false}
);

const HeaderProfile = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/HeaderProfile'
    ),
  {
    loading: () => <Loader />,
  }
);

interface LayoutProps {
  page?: string;
  meta?: ReactNode;
  children: ReactNode;
  HeaderComp?: FC;
  showbottom?: boolean;
  isMobile?: boolean;
  showFreeProBannerToday?: boolean;
  isCohortEligible?: boolean;
}

const NavLayout = ({HeaderComp, ...props}: LayoutProps) => {
  const theme = useTheme();
  const bannerHeight = useInfoBannerHeight();
  const isLargeDevice = useIsLargeDevice();
  const isAboveXL = useIsLargeDevice(1442);
  const isSmall = useIsCustomWidth(500);
  const [promotionType, setPromotionType] = useState('');
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const currentPage = useSelector(
    (state: AppState) => state.app.page,
    shallowEqual
  );
  // Set by the JobFeed layout when a listing uses numbered pagination.
  const paginationEnabled = useSelector(
    (state: AppState) => state.app.layout.paginationEnabled,
    shallowEqual
  );

  const {children, page} = props || {
    page: 'all',
  };

  const cookieBannerData = {
    policyLinks: {
      privacyPolicy: 'https://www.iimjobs.com/privacy/',
      cookiePolicy: 'https://www.iimjobs.com/cookiepolicy',
    },
    CTA: 'Got it',
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const userAgentInfo = utilServerSideDeviceDetection({});
      sessionStorage.setItem(
        'device',
        userAgentInfo.isMobile ? 'mobile' : 'web'
      );
      sessionStorage.setItem('deviceType', userAgentInfo.deviceType || 'web');
    }
  }, []);
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const [showHiristPromotionBanner, setShowHiristPromotionBanner] = useState(
    props?.isMobile
  );

  const [url, setUrl] = useState('');
  const [cookieValue, setCookieValue] = useState('');
  const [hideBanner, setHideBanner] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    const cookies = cookie.parse(document?.cookie);
    const myCookieValue = cookies['linkage_banner'];
    setCookieValue(myCookieValue);
    const bannerData = getItemWithExpiry('HIDE_APP_PROMO_BANNER');
    setHideBanner(bannerData);
  }, []);

  useEffect(() => {
    if (
      (currentPage === TAG ||
        currentPage === SEARCH ||
        currentPage === JOBDETAIL ||
        (currentPage === JOBFEED &&
          !url.includes('/company') &&
          !url.includes('/courses'))) &&
      cookieValue != '1' &&
      isSmall &&
      !isAppIimjobs
    ) {
      setShowHiristPromotionBanner(true);
    } else {
      setShowHiristPromotionBanner(false);
    }
  }, [currentPage, url, isSmall]);

  const handleNewLinkageBanner = () => {
    setShowHiristPromotionBanner(false);
    // Dispatch custom event to notify other components that banner is closed
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('hiristBannerClosed'));
    }
  };

  const showBottomPromoSpacer =
    currentPage !== JOBDETAIL &&
    currentPage !== COURSE &&
    !(currentPage?.indexOf('epoch') > -1) &&
    !page?.includes('registration-') &&
    isSmall &&
    !showCookieBanner &&
    !!props.showbottom &&
    !hideBanner &&
    !!promotionType;

  // Talent Bridge banner: mobile web, hirist, jobfeed page only. It sits in the
  // fixed top stack, between the download-app banner and the nav bar.
  const showTalentBridgeBanner =
    !isAppIimjobs &&
    isSmall &&
    currentPage === JOBFEED &&
    !url.includes('/company') &&
    !url.includes('/courses');

  const tbBannerRef = useRef<HTMLDivElement | null>(null);
  const [tbBannerHeight, setTbBannerHeight] = useState(0);

  // Measure the banner so the fixed nav bar / content can be pushed below it.
  useEffect(() => {
    const el = tbBannerRef.current;
    if (
      !showTalentBridgeBanner ||
      !el ||
      typeof ResizeObserver === 'undefined'
    ) {
      setTbBannerHeight(0);
      return;
    }
    const update = () => setTbBannerHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showTalentBridgeBanner, url, currentPage]);

  // Expose the height so the (shared) fixed header can offset its `top` via
  // `var(--tb-banner-h, 0px)` — defaults to 0px everywhere else.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--tb-banner-h', `${tbBannerHeight}px`);
    return () => {
      root.style.setProperty('--tb-banner-h', '0px');
    };
  }, [tbBannerHeight]);

  return (
    <>
      {!isAppIimjobs && isSmall && showHiristPromotionBanner && (
        <HiristPromotions onLinkageCrossClick={handleNewLinkageBanner} />
      )}
      {/* Talent Bridge banner: fixed, directly below the download-app banner
          and above the nav bar (mobile web, hirist, jobfeed page only). */}
      {showTalentBridgeBanner && (
        <Box
          ref={tbBannerRef}
          sx={{
            position: 'fixed',
            top: showHiristPromotionBanner && !isAppIimjobs ? '80px' : 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <TalentBridgePromoSlider variant="banner" />
        </Box>
      )}
      {page === HOME ? (
        <HeaderNonAuth />
      ) : page === EPOCH ? (
        <HeaderProfile />
      ) : HeaderComp ? (
        <HeaderComp />
      ) : (
        <HeaderAuth
          showHiristPromotionBanner={showHiristPromotionBanner && !isAppIimjobs}
        />
      )}
      <Box
        pt={
          bannerHeight / 8 +
          (showHiristPromotionBanner && !isAppIimjobs
            ? currentPage === JOBDETAIL
              ? 12
              : 10
            : 0) +
          tbBannerHeight / 8
        }
      />
      {children}
      {currentPage !== JOBDETAIL &&
        currentPage !== COURSE &&
        !(currentPage?.indexOf('epoch') > -1) &&
        !page?.includes('registration-') &&
        isSmall &&
        !showCookieBanner && (
          <Promotions
            theme={theme}
            showFreeProBannerToday={props.showFreeProBannerToday}
            isCohortEligible={props.isCohortEligible}
            showbottom={props.showbottom}
            setPromotionType={setPromotionType}
            style={{
              position: 'fixed',
              width: '100%',
              paddingLeft: theme.spacing(1.875),
              paddingRight: theme.spacing(1.875),
              bottom: '25px',
              zIndex: 12,
            }}
          />
        )}
      {!page?.includes('registration-') && !page?.includes('cv-analyzer') && (
        <ScrollToTop
          theme={theme}
          style={{
            right: isAppIimjobs ? (isAboveXL ? '136px' : '7px') : '0px',
            bottom: !isAppIimjobs
              ? isLargeDevice
                ? '56px'
                : !hideBanner && currentPage !== COURSE
                ? '176px'
                : '70px'
              : isLargeDevice
              ? currentPage === JOBDETAIL
                ? '100px'
                : '60px'
              : page === COURSE || currentPage === JOBDETAIL
              ? '150px'
              : promotionType === 'Install Now'
              ? '150px'
              : promotionType === 'Get App'
              ? '160px'
              : '17px',
            position: 'fixed',
            zIndex: 12,
            cursor: 'pointer',
          }}
        />
      )}
      {(isAppIimjobs || currentPage === SITEMAP) && <Footer />}

      {!isAppIimjobs && paginationEnabled && !isLargeDevice && (
        <HomePageFooter />
      )}
      {showBottomPromoSpacer && <Box height="120px" />}
      {page === HOME && (
        <CookieBanner
          showCookieBanner={showCookieBanner}
          setShowCookieBanner={setShowCookieBanner}
          policyLinks={cookieBannerData.policyLinks}
          CTA={cookieBannerData.CTA}
        />
      )}
    </>
  );
};

export default NavLayout;
